import type { VxeGlobalI18nLocale } from 'vxe-table'
// src/i18n.ts
import api from '@/api'

import { createI18n } from 'vue-i18n'
import VxeUI from 'vxe-table'
import enVxe from 'vxe-table/lib/locale/lang/en-US'
import zhVxe from 'vxe-table/lib/locale/lang/zh-CN'
// ⚠️ 语言文件名、键名统一用小写破折号写法
import enUs from './lang/en-us.json'
import zhCn from './lang/zh-cn.json'

/* ────────────── 1. 语言配置可做下拉列表 ────────────── */
export const localeOptions = {
  'zh-cn': { name: '中文（简体）', locale: 'zh-cn' },
  'en-us': { name: 'English', locale: 'en-us' },
}

/* ────────────── 2. 静态引入所有语言包 ────────────── */
const messages = {
  'zh-cn': zhCn,
  'en-us': enUs,
}

VxeUI.setI18n(<VxeGlobalI18nLocale>'zh-cn', zhVxe)
VxeUI.setI18n(<VxeGlobalI18nLocale>'en-us', enVxe)

/* ────────────── 3. 浏览器/本地语言探测 ────────────── */
async function detectLocale(): Promise<string> {
  // 默认使用中文
  const defaultLocale = 'zh-cn'
  const allow = Object.keys(localeOptions)

  // 首先检查本地存储中是否已有用户设置的语言
  const saved = localStorage.getItem('lang')?.toLowerCase()
  if (saved && allow.includes(saved)) {
    // 如果本地已有保存的语言设置，优先使用它
    return saved
  }

  try {
    // 如果本地没有语言设置，尝试从后端获取用户语言偏好
    const res = await api.get('app/user/locale')
    const userLocale = res.data.locale?.toLowerCase()

    // 检查后端返回的语言是否在支持列表中
    if (userLocale && allow.includes(userLocale)) {
      localStorage.setItem('lang', userLocale)
      VxeUI.setLanguage(userLocale)
      return userLocale
    }
  }
  catch (error) {
    console.error('获取用户语言偏好失败:', error)
  }

  // 最后默认返回中文
  localStorage.setItem('lang', defaultLocale)
  return defaultLocale
}

/* ────────────── 4. 初始化并持久化语言 ────────────── */
// 先检查 localStorage 中是否已有语言设置，有则使用，否则使用默认中文
const saved = localStorage.getItem('lang')?.toLowerCase()
const allow = Object.keys(localeOptions)
const initLocale = (saved && allow.includes(saved)) ? saved : 'zh-cn'
// 只有在没有保存语言设置的情况下才设置默认值
if (!saved) {
  localStorage.setItem('lang', initLocale)
}

/* ────────────── 5. 创建 i18n 实例 ────────────── */
const i18n = createI18n({
  legacy: false,
  globalInjection: true, // 允许在模板中直接使用 $t
  locale: initLocale,
  fallbackLocale: 'en-us',
  warnHtmlMessage: false,
  missingWarn: false, // 关闭缺失 key 的警告
  fallbackWarn: false, // 关闭 fallback 到其他语言的警告
  messages,
})

VxeUI.setLanguage(<VxeGlobalI18nLocale>initLocale)

// 在应用启动后获取用户语言偏好并更新
detectLocale().then((locale) => {
  // 无论是否与初始值相同，都设置为检测到的语言
  // 这确保了语言设置的一致性
  i18n.global.locale.value = locale as 'en-us' | 'zh-cn'
  VxeUI.setLanguage(<VxeGlobalI18nLocale>locale)
})

export default i18n
